import { PaymentMethod } from "@/app/billing/payment-method";
import i18n from "@/app/i18n";
import {
  faCcMastercard,
  faCcStripe,
  faCcVisa,
} from "@fortawesome/free-brands-svg-icons";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CardElement, useElements } from "@stripe/react-stripe-js";
import { DateTime } from "luxon";
import React, { Fragment, useEffect, type FC } from "react";

export type PaymentMethodSelectProps = {
  paymentMethods: Array<PaymentMethod>;
  onPaymentMethodChange: (paymentMethodId: string) => void;
};

export const PaymentMethodSelect: FC<PaymentMethodSelectProps> = ({
  paymentMethods,
  onPaymentMethodChange,
}) => {
  const elements = useElements();
  const [paymentMethodId, setPaymentMethodId] = React.useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    onPaymentMethodChange(paymentMethodId);

    if (paymentMethodId !== undefined) {
      elements.getElement(CardElement).clear();
    }
  }, [paymentMethodId]);

  return (
    <ul className="flex flex-col gap-4">
      <li
        onClick={() => setPaymentMethodId(undefined)}
        className="flex rounded-md shadow-sm h-16"
      >
        <div
          className={`${paymentMethodId === undefined ? "bg-primary" : "bg-gray-500"} flex w-16 flex-shrink-0 items-center justify-center rounded-l-md text-sm font-medium text-white`}
        >
          <FontAwesomeIcon icon={faAdd} className="text-2xl" />
        </div>
        <div className="flex flex-1 items-center justify-between truncate rounded-r-md border-b border-r border-t border-gray-200 bg-white">
          <CardElement
            className="flex-grow px-4"
            onFocus={() => setPaymentMethodId(undefined)}
            options={{
              hideIcon: true,
              disableLink: true,
              style: {
                base: {
                  fontSize: "1.25rem",
                  lineHeight: "36px",
                },
              },
            }}
          />
        </div>
      </li>
      {paymentMethods.map((paymentMethod) => (
        <li
          key={paymentMethod.id}
          onClick={() => setPaymentMethodId(paymentMethod.id)}
          className="flex rounded-md shadow-sm h-16"
        >
          <div
            className={`${paymentMethod.id === paymentMethodId ? "bg-primary" : "bg-gray-500"} flex w-16 flex-shrink-0 items-center justify-center rounded-l-md text-sm font-medium text-white`}
          >
            {paymentMethod?.type == "card" && (
              <Fragment>
                {paymentMethod?.card.brand == "visa" && (
                  <FontAwesomeIcon icon={faCcVisa} className="text-2xl" />
                )}
                {paymentMethod?.card.brand == "mastercard" && (
                  <FontAwesomeIcon icon={faCcMastercard} className="text-2xl" />
                )}
              </Fragment>
            )}
            {paymentMethod?.type == "sepa_debit" && (
              <Fragment>
                <FontAwesomeIcon icon={faCcStripe} className="text-2xl" />
              </Fragment>
            )}
          </div>
          <div className="flex flex-1 items-center justify-between truncate rounded-r-md border-b border-r border-t border-gray-200 bg-white">
            <div className="flex-1 truncate px-4 py-2 text-sm">
              <a className="font-medium text-gray-900 hover:text-gray-600">
                {paymentMethod?.type == "card"
                  ? `**** **** **** ${paymentMethod?.card.last4}`
                  : ""}
                {` - ${paymentMethod?.card?.expirationMonth}/${paymentMethod?.card?.expirationYear}`}
              </a>
              <div className="flex gap-4">
                <p className="text-gray-500">
                  {DateTime.fromISO(paymentMethod.createdAt)
                    .setLocale(i18n.language)
                    .toFormat("d MMMM yyyy")}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 pr-2"></div>
          </div>
        </li>
      ))}
    </ul>
  );
};
